import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, DownloadSimple, Trash, EnvelopeSimple, CaretRight, Bell, Lock, At, Eye, EyeSlash, CheckCircle, Globe, Users } from "@phosphor-icons/react";
import { PassportLogo } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { accountApi, prefsApi, authApi, languageApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, applyDocumentDir } from "@/i18n";

const getPasswordRules = (t) => [
    { id: "length",  label: t("settings.pwRules.length"),  test: (pw) => pw.length >= 8 },
    { id: "upper",   label: t("settings.pwRules.upper"),   test: (pw) => /[A-Z]/.test(pw) },
    { id: "lower",   label: t("settings.pwRules.lower"),   test: (pw) => /[a-z]/.test(pw) },
    { id: "number",  label: t("settings.pwRules.number"),  test: (pw) => /[0-9]/.test(pw) },
    { id: "special", label: t("settings.pwRules.special"), test: (pw) => /[!@#$%^&*()_+\-=\[\]{}|;':",./\<>?]/.test(pw) },
];

export default function Settings() {
    const { user, loading, setUser } = useAuth();
    const nav = useNavigate();
    const { t, i18n } = useTranslation();
    const passwordRules = useMemo(() => getPasswordRules(t), [t]);
    const [digest, setDigest] = useState(true);
    const [isPrivate, setIsPrivate] = useState(false);
    const [savingPref, setSavingPref] = useState(false);
    const [savingPrivacy, setSavingPrivacy] = useState(false);
    const [savingLang, setSavingLang] = useState(false);
    const [currentLang, setCurrentLang] = useState(i18n.language?.slice(0, 2) || "en");

    // Change Password state
    const [currentPw, setCurrentPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmNewPw, setConfirmNewPw] = useState("");
    const [showCurrentPw, setShowCurrentPw] = useState(false);
    const [showNewPw, setShowNewPw] = useState(false);
    const [savingPw, setSavingPw] = useState(false);

    // Change Handle state
    const [newHandle, setNewHandle] = useState("");
    const [savingHandle, setSavingHandle] = useState(false);

    useEffect(() => {
        if (!user) return;
        prefsApi.get().then((r) => {
            setDigest(r.email_digest_enabled !== false);
            setIsPrivate(r.is_private === true);
            // Sync saved language from backend on page load
            if (r.preferred_language && r.preferred_language !== i18n.language?.slice(0, 2)) {
                i18n.changeLanguage(r.preferred_language);
                setCurrentLang(r.preferred_language);
                applyDocumentDir(r.preferred_language);
                localStorage.setItem("passport_language", r.preferred_language);
            }
        }).catch(() => {});
    }, [user]); // Removed i18n to prevent re-fetching on language change

    const toggleDigest = async () => {
        const next = !digest;
        setDigest(next);
        setSavingPref(true);
        try {
            await prefsApi.update({ email_digest_enabled: next });
            toast.success(next ? t("settings.toastDigestOn") : t("settings.toastDigestOff"));
        } catch {
            setDigest(!next);
            toast.error(t("settings.toastPrefError"));
        } finally {
            setSavingPref(false);
        }
    };

    const togglePrivacy = async () => {
        const next = !isPrivate;
        setIsPrivate(next);
        setSavingPrivacy(true);
        try {
            await prefsApi.update({ is_private: next });
            toast.success(next ? "Profile set to Friends Only." : "Profile set to Public.");
        } catch {
            setIsPrivate(!next);
            toast.error("Could not update privacy. Please try again.");
        } finally {
            setSavingPrivacy(false);
        }
    };
    const changeLanguage = async (langCode) => {
        setSavingLang(true);
        const prev = currentLang;
        try {
            // Apply immediately so the user sees the change before we even hit the server
            i18n.changeLanguage(langCode);
            setCurrentLang(langCode);
            applyDocumentDir(langCode);
            await languageApi.set(langCode);
            toast.success(t("settings.toastLangSuccess"));
        } catch {
            // Revert on failure
            i18n.changeLanguage(prev);
            setCurrentLang(prev);
            applyDocumentDir(prev);
            toast.error(t("settings.toastLangError"));
        } finally {
            setSavingLang(false);
        }
    };

    if (loading) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;
    if (!user) return <Navigate to="/auth" />;

    const isGoogleUser = user.has_password === false;

    const downloadData = async () => {
        try {
            toast.message(t("settings.toastPrepData"));
            const blob = await accountApi.exportData();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `passport-data-${user.handle}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(t("settings.toastDownloadStarted"));
        } catch (e) {
            toast.error(t("settings.toastExportError"));
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        const failing = passwordRules.filter((r) => !r.test(newPw));
        if (failing.length > 0) { toast.error(failing[0].label + " " + t("settings.isRequired")); return; }
        if (newPw !== confirmNewPw) { toast.error(t("settings.pwNoMatch")); return; }
        setSavingPw(true);
        try {
            await accountApi.changePassword(currentPw, newPw);
            toast.success(t("settings.pwUpdatedSuccess"));
            setCurrentPw(""); setNewPw(""); setConfirmNewPw("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("settings.pwUpdateError"));
        } finally {
            setSavingPw(false);
        }
    };

    const handleChangeHandle = async (e) => {
        e.preventDefault();
        setSavingHandle(true);
        try {
            const res = await accountApi.updateHandle(newHandle);
            toast.success(t("settings.handleUpdatedSuccess", { handle: res.handle }));
            // Refresh user context so header/nav updates instantly
            const updatedUser = await authApi.me();
            if (setUser) setUser(updatedUser);
            setNewHandle("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("settings.handleUpdateError"));
        } finally {
            setSavingHandle(false);
        }
    };

    const ROWS = [
        { icon: ShieldCheck, label: t("settings.privacyPolicy"), sub: t("settings.privacyPolicySub"), onClick: () => nav("/privacy"), testId: "settings-privacy" },
        { icon: FileText, label: t("settings.termsOfService"), sub: t("settings.termsOfServiceSub"), onClick: () => nav("/terms"), testId: "settings-terms" },
        { icon: DownloadSimple, label: t("settings.downloadData"), sub: t("settings.downloadDataSub"), onClick: downloadData, testId: "settings-download" },
        { icon: EnvelopeSimple, label: t("settings.contactPrivacy"), sub: "privacy@passporttravelnetwork.com", href: "mailto:privacy@passporttravelnetwork.com", testId: "settings-contact" },
        { icon: Trash, label: t("settings.deleteAccount"), sub: t("settings.deleteAccountSub"), onClick: () => nav("/delete-account"), danger: true, testId: "settings-delete" },
    ];

    const newPwAllPassed = passwordRules.every((r) => r.test(newPw));

    return (
        <div className="min-h-screen grain" data-testid="settings-page">
            <div className="max-w-2xl mx-auto px-6 md:px-10 py-12">
                <Link to="/app" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--gold)] mb-10" data-testid="settings-back">
                    <ArrowLeft size={14} /> {t("settings.backToDashboard")}
                </Link>

                <div className="flex items-center gap-3 mb-8">
                    <PassportLogo size={40} />
                    <div className="overline">{t("conciergeApp.passport")} / {t("settings.title")}</div>
                </div>

                <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tighter leading-[0.95] mb-3">{t("settings.title")}</h1>
                <p className="text-white/60 text-lg mb-12">{t("settings.signedInAs", { handle: user.handle, email: user.email })}</p>

                {/* ---- Privacy & Visibility ---- */}
                <div className="overline mb-4">/ Privacy &amp; Visibility</div>
                <div className="rounded-3xl p-6 mb-12" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <Users size={18} color="#D4AF37" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-white font-medium">Private Profile</div>
                                    <div className="text-white/50 text-xs mt-0.5">Friends Only</div>
                                </div>
                                {/* Toggle switch */}
                                <button
                                    onClick={togglePrivacy}
                                    disabled={savingPrivacy}
                                    className="relative w-11 h-6 rounded-full transition-colors shrink-0"
                                    style={{ background: isPrivate ? "var(--gold)" : "rgba(255,255,255,0.12)" }}
                                    data-testid="privacy-toggle"
                                    aria-label="Toggle private profile"
                                >
                                    <span
                                        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                                        style={{ transform: isPrivate ? "translateX(20px)" : "translateX(0)" }}
                                    />
                                </button>
                            </div>
                            <p className="text-white/40 text-xs mt-3 leading-relaxed">
                                When enabled, only accepted friends can view your posts, reels, and travel milestones. Your username, profile photo, and bio remain publicly discoverable.
                            </p>
                        </div>
                    </div>
                </div>

                {/* ---- Language & Region ---- */}
                <div className="overline mb-4">/ {t("settings.language")}</div>
                <div className="rounded-3xl p-6 mb-12" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <Globe size={18} color="#D4AF37" />
                        </div>
                        <div>
                            <div className="text-sm text-white font-medium">{t("settings.language")}</div>
                            <div className="text-white/50 text-xs mt-0.5">{t("settings.languageSub")}</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {SUPPORTED_LANGUAGES.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => changeLanguage(lang.code)}
                                disabled={savingLang}
                                className="flex flex-col items-start px-4 py-3 rounded-2xl text-left transition-all"
                                style={{
                                    background: currentLang === lang.code ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.04)",
                                    border: `1px solid ${currentLang === lang.code ? "rgba(212,175,55,0.6)" : "rgba(255,255,255,0.08)"}`,
                                }}
                                data-testid={`lang-${lang.code}`}
                            >
                                <span className="text-sm font-medium" style={{ color: currentLang === lang.code ? "var(--gold)" : "white" }}>{lang.nativeLabel}</span>
                                <span className="text-[11px] text-white/40 mt-0.5">{lang.label}</span>
                                {currentLang === lang.code && <CheckCircle size={12} weight="fill" color="#D4AF37" className="mt-1" />}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ---- Change Username ---- */}
                <div className="overline mb-4">{t("settings.usernameOverline")}</div>
                <div className="rounded-3xl p-6 mb-12" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <At size={18} color="#D4AF37" />
                        </div>
                        <div>
                            <div className="text-sm text-white font-medium">{t("settings.yourHandle")}</div>
                            <div className="text-white/50 text-xs mt-0.5">{t("settings.currentlyHandle", { handle: user.handle })}</div>
                        </div>
                    </div>
                    <form onSubmit={handleChangeHandle} className="space-y-3" data-testid="change-handle-form">
                        <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm select-none">@</span>
                            <input
                                value={newHandle}
                                onChange={(e) => setNewHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ""))}
                                placeholder={user.handle}
                                maxLength={20}
                                className="w-full bg-black/30 border rounded-xl px-4 py-3 pl-8 text-sm text-white focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                data-testid="new-handle-input"
                            />
                            {newHandle.length >= 3 && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: "var(--gold)" }}>
                                    passport.com/profile/{newHandle}
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={savingHandle || newHandle.length < 3}
                            className="btn-gold text-sm !py-2.5 !px-6"
                            data-testid="change-handle-submit"
                        >
                            {savingHandle ? t("common.saving") : t("settings.updateHandle")}
                        </button>
                    </form>
                </div>

                {/* ---- Change Password ---- */}
                <div className="overline mb-4">{t("settings.securityOverline")}</div>
                <div className="rounded-3xl p-6 mb-12" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                            <Lock size={18} color="#D4AF37" />
                        </div>
                        <div>
                            <div className="text-sm text-white font-medium">{t("settings.changePassword")}</div>
                            <div className="text-white/50 text-xs mt-0.5">{t("settings.updatePasswordSub")}</div>
                        </div>
                    </div>

                    {isGoogleUser ? (
                        <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                            <CheckCircle size={18} color="#4ade80" weight="fill" />
                            <div className="text-sm text-white/60">{t("settings.googlePwNotice")}</div>
                        </div>
                    ) : (
                        <form onSubmit={handleChangePassword} className="space-y-3" data-testid="change-password-form">
                            {/* Current password */}
                            <div className="relative">
                                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-1.5">{t("settings.currentPassword")}</label>
                                <input
                                    required type={showCurrentPw ? "text" : "password"}
                                    value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full bg-black/30 border rounded-xl px-4 py-3 pr-12 text-sm text-white focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                                    style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                    data-testid="current-pw-input"
                                />
                                <button type="button" onClick={() => setShowCurrentPw((v) => !v)} className="absolute right-3 bottom-3 text-white/40 hover:text-white/80 transition-colors" tabIndex={-1} data-testid="current-pw-eye">
                                    {showCurrentPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {/* New password */}
                            <div className="relative">
                                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-1.5">{t("settings.newPassword")}</label>
                                <input
                                    required type={showNewPw ? "text" : "password"}
                                    value={newPw} onChange={(e) => setNewPw(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full bg-black/30 border rounded-xl px-4 py-3 pr-12 text-sm text-white focus:outline-none focus:border-[color:var(--gold)] transition-colors"
                                    style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                    data-testid="new-pw-input"
                                />
                                <button type="button" onClick={() => setShowNewPw((v) => !v)} className="absolute right-3 bottom-3 text-white/40 hover:text-white/80 transition-colors" tabIndex={-1} data-testid="new-pw-eye">
                                    {showNewPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {/* Strength meter */}
                            {newPw.length > 0 && (
                                <div className="rounded-xl p-3 space-y-1.5" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                    <div className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: newPwAllPassed ? "#4ade80" : "rgba(255,255,255,0.4)" }}>
                                        {newPwAllPassed ? t("settings.looksGreat") : t("settings.requirements")}
                                    </div>
                                    {passwordRules.map((rule) => {
                                        const passed = rule.test(newPw);
                                        return (
                                            <div key={rule.id} className="flex items-center gap-2 text-[11px]" style={{ color: passed ? "#4ade80" : "rgba(255,255,255,0.45)" }}>
                                                {passed ? <CheckCircle size={12} weight="fill" color="#4ade80" /> : <span className="w-3 h-3 rounded-full border border-white/20 inline-block" />}
                                                {rule.label}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Confirm new password */}
                            <div>
                                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-1.5">{t("settings.confirmNewPassword")}</label>
                                <input
                                    required type="password"
                                    value={confirmNewPw} onChange={(e) => setConfirmNewPw(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full bg-black/30 border rounded-xl px-4 py-3 text-sm text-white focus:outline-none transition-colors"
                                    style={{ borderColor: confirmNewPw.length > 0 ? (confirmNewPw === newPw ? "rgba(74,222,128,0.5)" : "rgba(239,68,68,0.5)") : "rgba(255,255,255,0.1)" }}
                                    data-testid="confirm-new-pw-input"
                                />
                                {confirmNewPw.length > 0 && confirmNewPw !== newPw && (
                                    <p className="text-[11px] text-red-400 mt-1">{t("settings.pwNoMatch")}</p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={savingPw}
                                className="btn-gold text-sm !py-2.5 !px-6"
                                data-testid="change-pw-submit"
                            >
                                {savingPw ? t("common.saving") : t("settings.updatePasswordBtn")}
                            </button>
                        </form>
                    )}
                </div>

                {/* ---- Privacy & Data ---- */}
                <div className="overline mb-4">{t("settings.privacyDataOverline")}</div>
                <div className="rounded-3xl overflow-hidden" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    {ROWS.map((r, i) => {
                        const Icon = r.icon;
                        const inner = (
                            <>
                                <div className="w-11 h-11 rounded-full grid place-items-center shrink-0"
                                    style={{ background: r.danger ? "rgba(192,57,43,0.12)" : "rgba(212,175,55,0.1)", border: `1px solid ${r.danger ? "rgba(192,57,43,0.4)" : "rgba(212,175,55,0.3)"}` }}>
                                    <Icon size={18} color={r.danger ? "#e74c3c" : "#D4AF37"} weight={r.danger ? "fill" : "regular"} />
                                </div>
                                <div className="flex-1 text-left">
                                    <div className={`text-sm ${r.danger ? "text-[#e74c3c]" : "text-white"}`}>{r.label}</div>
                                    <div className="text-white/50 text-xs mt-0.5">{r.sub}</div>
                                </div>
                                <CaretRight size={14} className="text-white/30" />
                            </>
                        );
                        const cls = "w-full flex items-center gap-4 p-5 transition-colors hover:bg-white/[0.03]" + (i > 0 ? " border-t" : "");
                        const style = { borderColor: "rgba(255,255,255,0.05)" };
                        return r.href ? (
                            <a key={r.label} href={r.href} className={cls} style={style} data-testid={r.testId}>{inner}</a>
                        ) : (
                            <button key={r.label} onClick={r.onClick} className={cls} style={style} data-testid={r.testId}>{inner}</button>
                        );
                    })}
                </div>

                {/* ---- Emails ---- */}
                <div className="overline mb-4 mt-12">{t("settings.emailsOverline")}</div>
                <div className="rounded-3xl p-5 flex items-center gap-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="settings-digest">
                    <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.3)" }}>
                        <Bell size={18} color="#D4AF37" />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm text-white">{t("settings.dailyDigestEmail")}</div>
                        <div className="text-white/50 text-xs mt-0.5">{t("settings.dailyDigestSub")}</div>
                    </div>
                    <button
                        onClick={toggleDigest}
                        disabled={savingPref}
                        role="switch"
                        aria-checked={digest}
                        data-testid="settings-digest-toggle"
                        className="relative w-14 h-8 rounded-full transition-colors shrink-0"
                        style={{ background: digest ? "var(--gold)" : "rgba(255,255,255,0.15)" }}
                    >
                        <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform" style={{ transform: digest ? "translateX(24px)" : "translateX(0)" }} />
                    </button>
                </div>
            </div>

            <style>{`
                input:-webkit-autofill,
                input:-webkit-autofill:hover,
                input:-webkit-autofill:focus,
                input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 1000px #080e18 inset !important;
                    -webkit-text-fill-color: #ffffff !important;
                    caret-color: #ffffff;
                    border-color: rgba(255,255,255,0.15) !important;
                }
            `}</style>
        </div>
    );
}
